import "../styles/Sidebar.css";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FaTachometerAlt,
  FaChartBar,
  FaCreditCard,
  FaUsers,
  FaBuilding,
  FaStore,
  FaUserTie,
  FaChartPie,
  FaLifeRing,
  FaSignOutAlt,
  FaTimes,
  FaChevronRight,
  FaUserPlus,
  FaFileExport,
  FaFileAlt,
  FaFileCsv,
  FaRegClock,
  FaClipboardCheck,
  FaTrophy,
  FaPlusCircle,
} from "react-icons/fa";
import qrivaLogo from "../resources/qr-icon-white.png";
import { useAuth } from "../context/AuthContext";
import { clearUserData, getUserData } from "../utils/authStorage";

const normalizeRoles = (value) => {
  if (!Array.isArray(value)) return [];
  return value.flat().filter(Boolean);
};

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const userData = getUserData();
  const user = userData?.user || userData || {}; // handle both { user: {...} } and {...}
  const userType = String(user?.user_type || user?.type || "").toLowerCase();
  const roles = normalizeRoles(user?.roles);

  const [logoutOpen, setLogoutOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null); // key of the currently expanded dropdown

  const isPathActive = (path) =>
    location.pathname === path ||
    (path !== "/dashboard" && location.pathname.startsWith(path + "/"));

  const isItemActive = (item) =>
    isPathActive(item.path) ||
    (item.children || []).some((child) => child.path && isPathActive(child.path));

  const goToChild = (item, child) => {
    if (child.path) {
      navigate(child.path);
    } else if (child.action) {
      navigate(`${item.path}?action=${child.action}`);
    }
  };

  const handleLogoutClick = () => setLogoutOpen(true);
  const handleCloseLogout = () => setLogoutOpen(false);
  const handleConfirmLogout = () => {
    localStorage.removeItem("token");
    clearUserData();
    logout();
    setLogoutOpen(false);
    navigate("/");
  };

  /**
   * Determine what to show based on user type and roles
   */
  const getVisibleMenuItems = () => {
    // Admin â€” show all
    if (userType === "admin") { //super admin usertype
      return ["dashboard", "organizations", "analytics", "users", "settings"];
    }
    if (userType === "org") { // organization admin usertype
      return ["dashboard", "agents", "merchants", "users", "transactions", "reporting"];
    }
    // Subordinate â€” conditional based on roles
    if (userType === "subordinate") {
      // view-only => all access
      if (roles.includes("view-only")) {
        return ["dashboard", "reporting", "agents", "merchants", "terminals"];
      }

      // merchant_onboarding only
      if (roles.length === 1 && roles.includes("merchant_onboarding")) {
        return ["dashboard", "reporting", "unbind", "merchants"];
      }

      // terminal_creation only
      if (roles.length === 1 && roles.includes("agent_onboarding")) {
        return ["dashboard", "agents"];
      }
    }

    // default (no access)
    return [];
  };

  const visibleItems = getVisibleMenuItems();
  const usersLabel = userType === "admin" ? "Users" : "User Management";

  const menuItems = [
    {
      key: "dashboard",
      label: "Dashboard",
      icon: <FaTachometerAlt />,
      path: "/dashboard",
    },
    {
      key: "transactions",
      label: "Transactions",
      icon: <FaCreditCard />,
      path: "/dashboard/transactions",
      children: [
        { label: "Export All CSV", icon: <FaFileCsv />, action: "export" },
      ],
    },
    {
      key: "organizations",
      label: "Organizations",
      icon: <FaBuilding />,
      path: "/dashboard/organizationlist",
      children: [
        { label: "Create Organization", icon: <FaPlusCircle />, action: "create" },
      ],
    },
    {
      key: "agents",
      label: "Agents",
      icon: <FaUserTie />,
      path: "/dashboard/agentslist",
      children: [
        { label: "Create Agent", icon: <FaUserPlus />, action: "create" },
        { label: "Export", icon: <FaFileExport />, action: "export" },
      ],
    },
    {
      key: "merchants",
      label: "Merchants",
      icon: <FaStore />,
      path: "/dashboard/merchantsdetails",
      children: [
        { label: "Pending Approval", icon: <FaClipboardCheck />, action: "approval" },
        { label: "Performance Tiers", icon: <FaTrophy />, action: "tiers" },
      ],
    },
    {
      key: "analytics",
      label: "Analytics",
      icon: <FaChartBar />,
      path: "/dashboard/analytics",
      // Analytics is super-admin only and the page itself is a "Coming soon"
      // placeholder, so the dropdown mirrors that instead of offering actions.
      children: [
        { label: "Coming soon…", icon: <FaRegClock />, disabled: true },
      ],
    },
    {
      key: "users",
      label: usersLabel,
      icon: <FaUsers />,
      path: "/dashboard/userslist",
      children: [
        { label: "Create User", icon: <FaUserPlus />, action: "create" },
      ],
    },
    {
      key: "reporting",
      label: "Reporting",
      icon: <FaChartPie />,
      path: "/dashboard/reporting",
      children: [
        { label: "New Report", icon: <FaFileAlt />, action: "new" },
      ],
    },
  ];

  // Auto-expand the dropdown for whichever section the current route belongs to.
  useEffect(() => {
    const match = menuItems.find(
      (item) => item.children && item.children.length && isItemActive(item)
    );
    if (match) setOpenMenu(match.key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const bottomItems = [
    {
      key: "support",
      label: "Support",
      icon: <FaLifeRing />,
      path: "/dashboard/support",
    },
    {
      key: "logout",
      label: "Logout",
      icon: <FaSignOutAlt />,
      action: handleLogoutClick,
    },
  ];

  const consoleLabel =
    userType === "org"
      ? "ORGANIZATION ADMIN CONSOLE"
      : userType === "admin"
      ? "SUPER ADMIN CONSOLE"
      : "Enterprise Suite";

  return (
    <div className="sidebar">
      <div className="sidebar-top">
        <div className="sidebar-brand" onClick={() => navigate("/dashboard")}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") navigate("/dashboard");
          }}
        >
          <div className="sidebar-logo">
            <img src={qrivaLogo} alt="QRiva" />
          </div>
          <div className="sidebar-brand__meta">
            <div className="sidebar-brand__title">QRiva</div>
            <div className="sidebar-brand__sub">{consoleLabel}</div>
          </div>
        </div>

        <ul>
          {menuItems
            .filter((item) => visibleItems.includes(item.key))
            .map((item) => {
              const hasChildren = item.children && item.children.length > 0;
              const expanded = openMenu === item.key;
              const active = isItemActive(item);

              if (!hasChildren) {
                return (
                  <li
                    key={item.key}
                    onClick={() => navigate(item.path)}
                    className={active ? "active" : ""}
                  >
                    {item.icon}
                    <span>{item.label}</span>
                  </li>
                );
              }

              return (
                <li key={item.key} className="sb-group">
                  <button
                    type="button"
                    className={`sb-group__head ${active ? "active" : ""}`}
                    onClick={() => {
                      navigate(item.path);
                      setOpenMenu(item.key);
                    }}
                    aria-expanded={expanded}
                  >
                    {item.icon}
                    <span>{item.label}</span>
                    <FaChevronRight
                      className={`sb-caret ${expanded ? "open" : ""}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenMenu((prev) => (prev === item.key ? null : item.key));
                      }}
                    />
                  </button>

                  <ul className={`sb-submenu ${expanded ? "open" : ""}`}>
                    {item.children.map((child) => (
                      <li
                        key={child.label}
                        className={
                          child.disabled
                            ? "sb-subitem sb-subitem--disabled"
                            : child.path && isPathActive(child.path) && location.pathname === child.path
                            ? "sb-subitem active"
                            : "sb-subitem"
                        }
                        onClick={child.disabled ? undefined : () => goToChild(item, child)}
                      >
                        {child.icon}
                        <span>{child.label}</span>
                      </li>
                    ))}
                  </ul>
                </li>
              );
            })}
        </ul>
      </div>

      <div className="sidebar-actions">
        <ul className="sidebar-bottom-menu">
          {bottomItems.map((item) => (
            <li
              key={item.key}
              onClick={() => (item.action ? item.action() : navigate(item.path))}
              className={location.pathname === item.path ? "active" : ""}
            >
              {item.icon}
              <span>{item.label}</span>
            </li>
          ))}
        </ul>

        {/* Logout Confirmation Modal */}
        {logoutOpen &&
          createPortal(
            <div className="sb-logout-overlay">
              <div
                className="sb-logout-scrim"
                onClick={handleCloseLogout}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && handleCloseLogout()}
              />
              <div className="sb-logout-dialog">
                <div className="sb-logout-dialog__header">
                  <h3 className="sb-logout-dialog__title">Logout</h3>
                  <button
                    className="sb-logout-dialog__close"
                    onClick={handleCloseLogout}
                    type="button"
                    aria-label="Close"
                  >
                    <FaTimes />
                  </button>
                </div>
                <div className="sb-logout-dialog__body">
                  <p className="sb-logout-dialog__text">
                    Are you sure you want to logout?
                  </p>
                  <p className="sb-logout-dialog__subtext">
                    You will be redirected to the login page.
                  </p>
                </div>
                <div className="sb-logout-dialog__footer">
                  <button
                    className="sb-logout-dialog__cancel"
                    type="button"
                    onClick={handleCloseLogout}
                  >
                    Cancel
                  </button>
                  <button
                    className="sb-logout-dialog__confirm"
                    type="button"
                    onClick={handleConfirmLogout}
                  >
                    Logout
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )}
      </div>
    </div>
  );
};

export default Sidebar;
